import type { JsonObject, JsonValue, ToolContext, ToolDef } from "@fabric-harness/sdk";
import type { DatabricksRawProtocolClient } from "./raw-protocol-client.js";

export type DatabricksManagedAgentKind =
  | "supervisor-agent"
  | "knowledge-assistant"
  | "agent-endpoint";

export interface DatabricksResponsesContent {
  type?: string;
  text?: string;
}

export interface DatabricksResponsesOutputItem {
  id?: string;
  type?: string;
  role?: string;
  content?: DatabricksResponsesContent[];
}

export interface DatabricksAgentEndpointResponse {
  id?: string;
  status?: string;
  output?: DatabricksResponsesOutputItem[];
  error?: JsonObject | null;
  metadata?: JsonObject;
}

export interface DatabricksAgentEndpointInvocation {
  input: string | JsonValue[];
  previousResponseId?: string;
  customInputs?: JsonObject;
  metadata?: JsonObject;
  signal?: AbortSignal;
}

/**
 * Bounded OpenResponses invocation for an existing Databricks agent endpoint. The endpoint remains
 * owned and permissioned by Databricks; Harness only treats it as a governed read/subagent tool.
 */
export class DatabricksAgentEndpointClient {
  constructor(private readonly client: DatabricksRawProtocolClient) {}

  invoke(
    endpointName: string,
    invocation: DatabricksAgentEndpointInvocation,
  ): Promise<DatabricksAgentEndpointResponse> {
    const endpoint = endpointId(endpointName);
    const previousResponseId = invocation.previousResponseId?.trim();
    return this.client.post<DatabricksAgentEndpointResponse>(
      `/serving-endpoints/${encodeURIComponent(endpoint)}/invocations`,
      {
        input: invocation.input,
        stream: false,
        ...(previousResponseId ? { previous_response_id: previousResponseId } : {}),
        ...(invocation.customInputs ? { custom_inputs: invocation.customInputs } : {}),
        ...(invocation.metadata ? { metadata: invocation.metadata } : {}),
      },
      {},
      { retry: "never", ...(invocation.signal ? { signal: invocation.signal } : {}) },
    );
  }
}

export interface DatabricksManagedAgentToolOptions {
  endpointName: string;
  kind: DatabricksManagedAgentKind;
  /** Stable Databricks resource id retained in governance metadata. */
  resourceId?: string;
  name?: string;
  description?: string;
  /** Maximum serialized terminal response admitted into model context. Default 1 MiB. */
  maxOutputBytes?: number;
}

export interface DatabricksManagedAgentToolResult {
  id?: string;
  status?: string;
  text: string;
  output: DatabricksResponsesOutputItem[];
}

/** Project an existing Supervisor Agent, Knowledge Assistant, or Responses-compatible endpoint. */
export function databricksManagedAgentTool(
  client: DatabricksAgentEndpointClient,
  options: DatabricksManagedAgentToolOptions,
): ToolDef<{ question: string; previousResponseId?: string }, DatabricksManagedAgentToolResult> {
  const endpoint = endpointId(options.endpointName);
  const resourceId = nonEmpty(options.resourceId ?? endpoint, "resourceId");
  const maxOutputBytes = positiveInteger(options.maxOutputBytes ?? 1024 * 1024, "maxOutputBytes");
  return {
    name: options.name ?? `databricks_${options.kind.replaceAll("-", "_")}`,
    description:
      options.description ??
      `Ask the existing Databricks ${options.kind.replaceAll("-", " ")} bound to this tool.`,
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: {
        question: { type: "string", minLength: 1 },
        previousResponseId: { type: "string", minLength: 1 },
      },
      required: ["question"],
    },
    metadata: {
      effect: "read",
      provider: "databricks",
      service: options.kind,
      governance: {
        resources: [{ kind: options.kind, value: resourceId }],
      },
    },
    async execute(input, context?: ToolContext) {
      const response = await client.invoke(endpoint, {
        input: nonEmpty(input.question, "question"),
        ...(input.previousResponseId
          ? { previousResponseId: nonEmpty(input.previousResponseId, "previousResponseId") }
          : {}),
        ...(context?.signal ? { signal: context.signal } : {}),
      });
      const output = response.output ?? [];
      const result: DatabricksManagedAgentToolResult = {
        ...(response.id ? { id: response.id } : {}),
        ...(response.status ? { status: response.status } : {}),
        text: databricksResponsesText(response),
        output,
      };
      const bytes = new TextEncoder().encode(JSON.stringify(result)).byteLength;
      if (bytes > maxOutputBytes) {
        throw new Error(
          `Databricks ${options.kind} response exceeded the configured ${maxOutputBytes}-byte model output limit.`,
        );
      }
      return result;
    },
  };
}

export function databricksResponsesText(response: DatabricksAgentEndpointResponse): string {
  return (response.output ?? [])
    .flatMap((item) => item.content ?? [])
    .filter((content) => content.type === "output_text" && typeof content.text === "string")
    .map((content) => content.text as string)
    .join("");
}

function endpointId(value: string): string {
  const normalized = nonEmpty(value, "endpointName");
  if (!/^[A-Za-z0-9][A-Za-z0-9._-]{0,254}$/u.test(normalized)) {
    throw new Error("endpointName must be a Databricks serving endpoint name.");
  }
  return normalized;
}

function nonEmpty(value: string, name: string): string {
  const normalized = value.trim();
  if (!normalized) throw new Error(`${name} cannot be empty.`);
  return normalized;
}

function positiveInteger(value: number, name: string): number {
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error(`${name} must be positive.`);
  return value;
}
